import { useState, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import { RenovationLabel } from "shared/types/renovation";

interface Props {
    value: RenovationLabel | null;
    onChange: (label: RenovationLabel | null) => void;
    onFocus?: () => void;
    placeholder?: string;
}

export const LabelSearch = ({ value, onChange, onFocus, placeholder = "Type om te zoeken" }: Props) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [labels, setLabels] = useState<RenovationLabel[]>([]);
    const [filteredLabels, setFilteredLabels] = useState<RenovationLabel[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { session } = useSession();

    useEffect(() => {
        fetchLabels();
    }, []);

    useEffect(() => {
        if (!searchTerm) {
            setFilteredLabels(labels);
            return;
        }
        const filtered = labels.filter(label =>
            label.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredLabels(filtered);
    }, [searchTerm, labels]);

    const fetchLabels = async () => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/renovation/labels`,
                { method: "GET" },
                session
            );
            if (!response.ok) throw new Error("Failed to fetch labels");
            const data = await response.json();
            setLabels(data);
            setFilteredLabels(data);
        } catch (error) {
            console.error("Error fetching labels:", error);
            toast.error("Failed to fetch labels");
        }
    };

    return (
        <div className="relative">
            <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsDropdownOpen(true);
                }}
                onFocus={() => {
                    setIsDropdownOpen(true);
                    onFocus?.();
                }}
                placeholder={placeholder}
                className="w-full rounded-lg border border-neutral-primary/20 bg-white px-3 py-2.5 text-sm focus:border-moss-primary focus:outline-hidden"
            />
            {value && (
                <div className="mt-2">
                    <div className="flex items-center gap-2 rounded-full bg-moss-primary/10 px-3 py-1 text-sm text-moss-primary">
                        {value.name}
                        <button
                            onClick={() => {
                                onChange(null);
                                setSearchTerm('');
                            }}
                            className="text-moss-primary hover:text-moss-primary/70"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}
            {isDropdownOpen && filteredLabels.length > 0 && (
                <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 max-h-48 overflow-auto rounded-lg border border-neutral-primary/20 bg-white shadow-lg">
                    {filteredLabels.map((label) => (
                        <div
                            key={label.id}
                            onClick={() => {
                                onChange(label);
                                setSearchTerm('');
                                setIsDropdownOpen(false);
                            }}
                            className="cursor-pointer px-3 py-2 text-sm hover:bg-neutral-primary/10"
                        >
                            {label.name}
                            <span className="ml-2 text-xs text-neutral-400">
                                {label.type.charAt(0) + label.type.slice(1).toLowerCase()}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}; 